import { Command } from '@/shared/commands/command.types'

import { getContentForRichtextCell } from '~shared/types/Cell'

import { toggleTaskItemChecked } from '@/features/richtext/nodes/taskitem/taskitem.utils'
import {
  createJSONContentFromHTMLContent,
  renderJSONContentAsHTMLString,
} from '@/features/richtext/tiptap.helpers'
import { useCellSnapshot } from '@/features/rundown/data/cells.store'

import { gridSaveActions, saveKeys } from '../../../grid/grid.save'
import { useCellTextSaveState } from '../../../grid/grid.save.hooks'
import { CellCommandContext, cellCommands } from '../../cell.commands'
import { useCellKey } from '../../cell.context'
import { useCellIsReadOnly, useCellIsSelected } from '../../cell.hooks'
import { CellToolbar } from '../../toolbar/CellToolbar'
import { CellUpload } from '../../upload/CellUpload'

import { RichtextPreviewRenderer } from './RichtextPreviewRenderer'

const PreviewCommands = [
  cellCommands.edit,
  cellCommands.history,
] satisfies Command<CellCommandContext>[]

/**
 * Render static preview of cell content.
 *
 * @note This is only for rich-text cells, as determined in the `<Cell>` component.
 */
export function RichtextColumnCellPreview() {
  const cellKey = useCellKey()
  const cell = useCellSnapshot()

  const cellContentAsHTML = getContentForRichtextCell(cell?.content)

  const optimisticContent = useCellTextSaveState(
    cellKey,
    cellContentAsHTML,
    cell?.updatedAt,
  )
  const content = optimisticContent.displayValue

  const isReadOnly = useCellIsReadOnly()

  // Block checkboxes while a save is pending (would conflict) or failed (would
  // resubmit the failed content). A failure is resolved with Retry or Abandon.
  const taskItemsBlocked = optimisticContent.isSaving
    ? 'pending'
    : optimisticContent.error
      ? 'error'
      : null

  const isSelected = useCellIsSelected()

  const handleTaskItemToggle = (taskItemIndex: number) => {
    const contentAsJSON = createJSONContentFromHTMLContent(content)
    const updatedJSON = toggleTaskItemChecked(contentAsJSON, taskItemIndex)

    gridSaveActions.commit(saveKeys.cell(cellKey), {
      kind: 'cellText',
      cellKey,
      content: {
        text: renderJSONContentAsHTMLString(updatedJSON),
      },
    })
  }

  return (
    <>
      <CellUpload>
        <RichtextPreviewRenderer
          contentAsHTML={content}
          onTaskItemToggle={isReadOnly ? undefined : handleTaskItemToggle}
          taskItemsBlocked={taskItemsBlocked}
          taskItemsFocusable={isSelected}
        />
      </CellUpload>
      <CellToolbar localCommands={PreviewCommands} />
    </>
  )
}
